import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// Same theme tokens as the rest of the site.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// NEW hero background — "flowing ribbons" motif, distinct from every
// other page's hero animation. Layered translucent wave paths drift
// horizontally at different speeds/depths, like streams of service
// offerings flowing together, with soft drifting light dots on top.
// ---------------------------------------------------------------------
function RibbonField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <DotGrid light />
      <span className="ribbon-glow" />

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path
          className="ribbon-path ribbon-path-1"
          d="M -10 30 C 15 20, 35 40, 50 28 C 65 16, 85 36, 110 24"
          fill="none"
          stroke={theme.accent}
          strokeOpacity="0.35"
          strokeWidth="0.6"
        />
        <path
          className="ribbon-path ribbon-path-2"
          d="M -10 52 C 18 66, 38 42, 55 54 C 72 66, 88 46, 110 58"
          fill="none"
          stroke={theme.gold}
          strokeOpacity="0.28"
          strokeWidth="0.5"
        />
        <path
          className="ribbon-path ribbon-path-3"
          d="M -10 74 C 20 62, 40 82, 58 70 C 76 58, 90 78, 110 68"
          fill="none"
          stroke="#FFFFFF"
          strokeOpacity="0.14"
          strokeWidth="0.4"
        />
      </svg>

      {Array.from({ length: 12 }).map((_, i) => (
        <span
          key={i}
          className="ribbon-dot"
          style={{
            left: `${(i * 41) % 100}%`,
            top: `${18 + ((i * 27) % 60)}%`,
            backgroundColor: i % 3 === 0 ? theme.gold : "#FFFFFF",
            animationDelay: `${i * 0.5}s`,
            animationDuration: `${5 + (i % 4)}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  useEffect(() => {
    fetch("http://localhost:5000/api/services")
      .then((res) => res.json())
      .then((data) => {
        setServices(data.services);
      })
      .catch((err) => console.log(err));
  }, []);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .ribbon-glow {
          position: absolute; top: 40%; left: 50%; width: 44rem; height: 44rem;
          margin: -22rem 0 0 -22rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}28, transparent 68%);
          filter: blur(70px);
          animation: ribbonGlowBreathe 7s ease-in-out infinite;
        }
        @keyframes ribbonGlowBreathe {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.12); }
        }

        .ribbon-path {
          animation: ribbonDrift 14s ease-in-out infinite;
        }
        .ribbon-path-1 { animation-duration: 16s; }
        .ribbon-path-2 { animation-duration: 20s; animation-direction: reverse; }
        .ribbon-path-3 { animation-duration: 24s; }
        @keyframes ribbonDrift {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(4px); }
        }

        .ribbon-dot {
          position: absolute; width: 3px; height: 3px; border-radius: 9999px;
          animation-name: ribbonDotFloat;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        @keyframes ribbonDotFloat {
          0%, 100% { opacity: 0.2; transform: translateY(0); }
          50% { opacity: 0.9; transform: translateY(-8px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .ribbon-glow, .ribbon-path, .ribbon-dot { animation: none !important; }
        }

        .card-glow {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }

        .img-mask { position: relative; }
        .img-mask::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%);
          transform: translateX(-140%) skewX(-12deg);
          transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }
        .group:hover .img-mask::after { transform: translateX(140%) skewX(-12deg); }
      `}</style>

      {/* ================= HERO (dark, ribbon field bg) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-28 md:pt-48 md:pb-32" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <RibbonField />
        </motion.div>

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow center light>What We Offer</Eyebrow>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white">
              <SplitText text="Our" className="text-white" />{" "}
              <span style={{ color: theme.accent }}>
                <SplitText text="Services" delay={0.2} />
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
            >
              We provide end-to-end digital solutions to help your business grow.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ================= SERVICES GRID (light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {services.length === 0 ? (
            <div className="text-center py-16" style={{ color: theme.inkMuted }}>
              Loading services…
            </div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              className="grid md:grid-cols-2 gap-8"
            >
              {services.map((service, i) => (
                <motion.div
                  key={i}
                  variants={staggerItem}
                  whileHover={{ y: -8 }}
                  className="group relative rounded-3xl overflow-hidden border transition-shadow duration-300 hover:shadow-xl"
                  style={{ backgroundColor: theme.surface, borderColor: theme.border }}
                >
                  {/* Image */}
                  <div className="img-mask h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(34,22,25,0.35), transparent 55%)" }} />
                  </div>

                  {/* Content */}
                  <div className="relative p-8">
                    <span className="card-glow" />
                    <h2 className="relative text-2xl font-bold mb-3" style={{ color: theme.accent }}>
                      {service.title}
                    </h2>

                    <p className="relative mb-6" style={{ color: theme.inkMuted }}>
                      {service.description}
                    </p>

                    {/* Sub Services */}
                    {service.sub && service.sub.length > 0 && (
                      <div className="relative grid grid-cols-2 gap-3 mb-6">
                        {service.sub.map((item, index) => (
                          <div
                            key={index}
                            className="text-sm px-4 py-2 rounded-full text-center"
                            style={{ backgroundColor: theme.surfaceAlt, color: theme.ink }}
                          >
                            {item}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Button */}
                    <Link
                      to={service.link}
                      data-cursor="hover"
                      style={{ backgroundColor: theme.accent }}
                      className="relative inline-flex items-center gap-2 px-8 py-3 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                    >
                      View Details
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Talk</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Need something" />
            <span className="block">
              <SplitText text="custom built?" delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Tell us what you're trying to build — we'll map it to the right service.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Start a Project
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}